// frontend/src/components/channels/edit-destination-dialog.tsx
// Read DESIGN.md before modifying. Title Case button labels; no em-dashes;
// Enabled/Disabled state words. Per-channel destination editor: route inbound
// to the Organization Destination, a custom webhook URL, or back to the
// HookMyApp CLI. Custom mode mirrors the channel-card webhook modal: it
// collects a verify token and the backend runs the same live handshake before
// applying. The parent owns the apply call + refresh; this modal only collects
// the choice.
import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { normalizeWebhookUrl } from '@/components/webhook-config-dialog';
import { webhookUrlSchema } from '@/lib/form/schemas';

type DestinationMode = 'org' | 'custom' | 'clear';

const MODE_ITEMS = [
  { value: 'org', label: 'Organization Destination' },
  { value: 'custom', label: 'Custom Webhook URL' },
  { value: 'clear', label: 'HookMyApp CLI' },
] as const;

interface EditDestinationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  channelName: string;
  /** Current webhook destination; null means the channel already routes to the CLI. */
  currentWebhookUrl: string | null;
  /** The org default destination URL, to tell "org" apart from "custom" when seeding. */
  orgWebhookUrl: string | null;
  /** Org destination must be set before a channel can point at it (else 409). */
  canSetOrgDest: boolean;
  applying: boolean;
  onConfirm: (mode: DestinationMode, url?: string, verifyToken?: string) => void;
}

export function EditDestinationDialog({
  open,
  onOpenChange,
  channelName,
  currentWebhookUrl,
  orgWebhookUrl,
  canSetOrgDest,
  applying,
  onConfirm,
}: EditDestinationDialogProps) {
  // Seed from the channel's current routing: no url reads as the CLI default,
  // the org default url as the org destination, any other url as custom. The
  // dialog is conditionally mounted per row, so the initializers reseed on
  // every open; no effect needed.
  const [mode, setMode] = useState<DestinationMode>(
    currentWebhookUrl === null ? 'clear' : currentWebhookUrl === orgWebhookUrl ? 'org' : 'custom',
  );
  const [customUrl, setCustomUrl] = useState(currentWebhookUrl ?? '');
  // Same default as the webhook modal's configure mode; the channel's current
  // token isn't on the org row, so the admin confirms what their server echoes.
  const [verifyToken, setVerifyToken] = useState('test123');

  const orgDisabled = !canSetOrgDest;
  const customInvalid =
    mode === 'custom' &&
    (!webhookUrlSchema.safeParse(normalizeWebhookUrl(customUrl)).success || !verifyToken.trim());

  return (
    <Dialog open={open} onOpenChange={(next) => !applying && onOpenChange(next)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Destination</DialogTitle>
          <DialogDescription>
            Choose where {channelName} delivers inbound events.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label>Destination</Label>
          <Select
            items={MODE_ITEMS}
            value={mode}
            onValueChange={(v) => setMode((v as DestinationMode) ?? 'clear')}
          >
            <SelectTrigger aria-label="Destination" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MODE_ITEMS.map((i) => (
                <SelectItem key={i.value} value={i.value} disabled={i.value === 'org' && orgDisabled}>
                  {i.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {orgDisabled && mode !== 'custom' && (
            <p className="text-xs text-muted-foreground">
              Set an organization destination first to route channels to it.
            </p>
          )}
        </div>

        {mode === 'custom' && (
          <>
            <div className="space-y-2">
              <Label htmlFor="custom-webhook-url">Webhook URL</Label>
              <Input
                id="custom-webhook-url"
                type="url"
                inputMode="url"
                placeholder="https://your-server.com/webhook"
                value={customUrl}
                onChange={(e) => setCustomUrl(e.target.value)}
                disabled={applying}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="custom-verify-token">Verify Token</Label>
              <Input
                id="custom-verify-token"
                value={verifyToken}
                onChange={(e) => setVerifyToken(e.target.value.trim())}
                disabled={applying}
              />
              <p className="text-xs text-muted-foreground">
                Your server must return this token in the body of a GET request.
              </p>
            </div>
          </>
        )}

        <DialogFooter>
          <Button variant="outline" disabled={applying} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={applying || (mode === 'org' && orgDisabled) || customInvalid}
            onClick={() =>
              onConfirm(
                mode,
                mode === 'custom' ? normalizeWebhookUrl(customUrl) : undefined,
                mode === 'custom' ? verifyToken.trim() : undefined,
              )
            }
          >
            {applying && <Loader2 className="size-3.5 animate-spin" />}
            {mode === 'custom' ? (applying ? 'Verifying...' : 'Verify & Save') : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
